/**
 * The drop zone, the file picker, and the clock that moves an upload.
 * Screen spec S13 zone 4, and 4.9b (the single narrated line).
 *
 * WHY THE PROGRESS ENGINE IS HERE AND NOT IN lib/mock/simulate. A timeline in
 * that engine is a scripted multi step operation with a step ledger and a log
 * tail, and every one of them is a named script. An upload is none of those:
 * it is one transfer of one file, spec 4.9 gives it the narrated line rather
 * than the checklist, and a person can start four of them at once, which a
 * single named script cannot represent.
 *
 * What it does borrow is the part that actually matters, and it is copied
 * deliberately rather than reinvented: progress is a pure function of
 * (now - startedAt) * speed, so nothing increments a counter. A tab left in
 * the background does not desync, a re-render mid transfer cannot double count
 * anything, and the demo's speed control is one number rather than a rewrite.
 *
 * THE SIZE CAP IS A NORMAL CONDITION, NOT AN ERROR. It is stated on the screen
 * before anyone drags anything, the same way the row cap is stated above a
 * table, and a file over it is refused before a byte moves rather than being
 * allowed to climb to 98% and then be told no.
 *
 * TOUCH: dragging is a desktop verb. The picker button is always present and
 * is the whole write path on a phone, per spec 2.7.
 */

import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../primitives/Button';
import type { ButtonVariant } from '../primitives/Button';
import { bytes as formatBytes } from '../../lib/format';
import { t } from '../../lib/i18n/t';
import { captureEvent } from '../../lib/analytics/posthog';
import type { FileUpload, Speed, UploadRefusal, UploadStatus } from '../../lib/types';
import '../../styles/components/upload-drop.css';

/** The per file cap, stated on the screen before anyone drags anything. */
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

/** Where a transfer that stopped got to. It stays there rather than resetting. */
export const STOPPED_AT = 0.62;

/** How often the tray repaints while anything is moving. */
export const UPLOAD_TICK_MS = 120;

/**
 * Kinds of file that file storage does not hold. Programs, in a person's
 * words. The list is short and explicit rather than an allow list, because an
 * allow list refuses the one file format nobody thought of and a person cannot
 * tell from the refusal whether it is them or us.
 */
const REFUSED_ENDINGS = ['exe', 'dmg', 'msi', 'app', 'pkg', 'bat', 'com', 'scr'];

export interface PickedFile {
  name: string;
  bytes: number;
  contentType: string;
}

/** What a person's own drop is checked against, before anything starts. */
export function refusalFor(file: PickedFile): UploadRefusal | null {
  const dot = file.name.lastIndexOf('.');
  const ending = dot === -1 ? '' : file.name.slice(dot + 1).toLowerCase();
  if (REFUSED_ENDINGS.includes(ending)) return 'wrong-type';
  if (file.bytes > MAX_UPLOAD_BYTES) return 'too-big';
  return null;
}

/** A plausible transfer time for a real file a person dropped. */
export function durationFor(bytes: number): number {
  return Math.round(1_200 + Math.min(7_800, bytes / 200));
}

export interface UploadProgress {
  status: UploadStatus;
  /** 0 to 1. */
  fraction: number;
}

export function uploadProgress(upload: FileUpload, now: number, speed: Speed): UploadProgress {
  // Refused by rule, so it never moved. Showing a track here would be a lie
  // about work that never started.
  if (upload.refusal === 'too-big' || upload.refusal === 'wrong-type') {
    return { status: 'didnt-upload', fraction: 0 };
  }

  const elapsed =
    speed === 'instant'
      ? Number.POSITIVE_INFINITY
      : Math.max(0, (now - upload.startedAt) * speed);

  const ceiling = upload.refusal === 'stopped' ? upload.durationMs * STOPPED_AT : upload.durationMs;
  const capped = Math.min(elapsed, ceiling);
  const fraction = upload.durationMs <= 0 ? 1 : Math.min(1, capped / upload.durationMs);

  if (elapsed < ceiling) return { status: 'uploading', fraction };
  return {
    status: upload.refusal === 'stopped' ? 'didnt-upload' : 'landed',
    fraction: upload.refusal === 'stopped' ? fraction : 1,
  };
}

/** The sentence a refusal gets. One line, no blame, and it says what to do. */
export function refusalSentence(refusal: UploadRefusal, size: number): string {
  if (refusal === 'too-big') {
    return t('Files can be up to {cap}. This one is {size}.', {
      cap: formatBytes(MAX_UPLOAD_BYTES),
      size: formatBytes(size),
    });
  }
  if (refusal === 'wrong-type') {
    return t('Your files hold images, documents and data. Programs are not stored here.');
  }
  return t('The transfer stopped partway. Nothing was saved.');
}

/**
 * One interval for the tray, running only while something is moving. It is the
 * same contract as the app's ticker: it returns "now" so the caller reads the
 * clock rather than keeping a copy of progress.
 */
export function useUploadClock(active: boolean): number {
  const [, bump] = useState(0);

  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => bump((n) => n + 1), UPLOAD_TICK_MS);
    return () => window.clearInterval(id);
  }, [active]);

  return Date.now();
}

/* ------------------------------------------------------------- drop zone */

export interface DropZoneProps {
  /**
   * The folder a drop lands in, with a leading slash: /images. Left out at the
   * top of the store, where the sentence is a whole sentence of its own rather
   * than one ending in a bare slash.
   */
  target?: string;
  /** True when uploads cannot start. The overlay says why instead of lying. */
  blocked?: boolean;
  blockedSentence?: string;
  onFiles: (files: PickedFile[]) => void;
  children: ReactNode;
}

/**
 * The whole content area is the target, per spec S13 zone 4. The counter is
 * what stops the overlay flickering: dragging over a child fires dragleave on
 * the parent, and a naive implementation blinks once per row crossed.
 */
export function DropZone({ target, blocked = false, blockedSentence, onFiles, children }: DropZoneProps) {
  const [over, setOver] = useState(false);
  const depth = useRef(0);

  const carriesFiles = (event: React.DragEvent) =>
    Array.from(event.dataTransfer?.types ?? []).includes('Files');

  const onDragEnter = useCallback((event: React.DragEvent) => {
    if (!carriesFiles(event)) return;
    event.preventDefault();
    depth.current += 1;
    setOver(true);
  }, []);

  const onDragOver = useCallback((event: React.DragEvent) => {
    if (!carriesFiles(event)) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = blocked ? 'none' : 'copy';
  }, [blocked]);

  const onDragLeave = useCallback(() => {
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setOver(false);
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      depth.current = 0;
      setOver(false);
      if (blocked) return;
      const picked = Array.from(event.dataTransfer?.files ?? []).map((file) => ({
        name: file.name,
        bytes: file.size,
        contentType: file.type,
      }));
      if (picked.length > 0) {
        captureEvent('files_dropped_to_zone', {
          count: picked.length,
          target_folder: target,
        });
        onFiles(picked);
      }
    },
    [blocked, onFiles, target],
  );

  return (
    <div
      className="dzone"
      data-over={over ? 'yes' : 'no'}
      data-blocked={blocked ? 'yes' : 'no'}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {children}

      {over ? (
        <div className="dzone__overlay" role="presentation">
          <p className="dzone__say">
            {blocked
              ? (blockedSentence ?? t('Uploads are paused right now.'))
              : target
                ? t('Drop to upload to {folder}', { folder: target })
                : t('Drop to upload')}
          </p>
        </div>
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------- picker */

export interface FilePickerButtonProps {
  label: string;
  variant?: ButtonVariant;
  disabled?: boolean;
  onFiles: (files: PickedFile[]) => void;
}

/**
 * The picker is the write path on a phone and the keyboard path everywhere.
 * A drag and drop zone with no button is a feature a keyboard user does not
 * have.
 */
export function FilePickerButton({
  label,
  variant = 'outline',
  disabled = false,
  onFiles,
}: FilePickerButtonProps) {
  const input = useRef<HTMLInputElement | null>(null);

  return (
    <>
      <Button
        variant={variant}
        disabled={disabled}
        onClick={() => {
          captureEvent('file_picker_opened');
          input.current?.click();
        }}
      >
        {label}
      </Button>
      <input
        ref={input}
        className="picker__input"
        type="file"
        multiple
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => {
          const picked = Array.from(event.target.files ?? []).map((file) => ({
            name: file.name,
            bytes: file.size,
            contentType: file.type,
          }));
          // The value is cleared so choosing the same file twice fires twice,
          // which is what a person expects after a transfer stopped.
          event.target.value = '';
          if (picked.length > 0) {
            captureEvent('file_picker_files_selected', { count: picked.length });
            onFiles(picked);
          }
        }}
      />
    </>
  );
}
